/**
 * 3PL usage CSV: exactly the SKU rows on screen (client, bill line, search and
 * sort already applied), every bucket split by whose stock, plus location and
 * dates on every row, and the row's amount last (new columns go at the end so
 * a step 1 spreadsheet keeps its column letters).
 *
 * Headers stay English, like the Consumption CSV, so spreadsheet formulas keep
 * working, and use real names: the 3PL's name comes from the response. The
 * production-redo column is in the 3PL's CSV only.
 *
 * CRLF, no BOM: `downloadCsvFile` (lib/download.ts) owns the BOM and is
 * idempotent about it, so prefixing here would fork that decision.
 */
import { toCsvContent } from '@/lib/csv';
import type { ThreePlUsageReport, ThreePlUsageRow, ThreePlUsageViewer } from './three-pl-usage-view';

export interface ThreePlUsageCsvContext {
  viewer: ThreePlUsageViewer;
  /** The 3PL owner's display name (`location.ownerName`). */
  ownerName: string;
  clientsById: ReadonlyMap<string, { name: string }>;
  location: Pick<ThreePlUsageReport['location'], 'name' | 'timezone'>;
  period: ThreePlUsageReport['period'];
  /** The response's currency (the 3PL's, D2): names the Amount column. */
  currency: string;
}

export function threePlUsageCsvHeaders(
  viewer: ThreePlUsageViewer,
  ownerName: string,
  currency: string,
): string[] {
  return [
    'Client',
    'SKU',
    'Product',
    'Shipped',
    'Printed on client stock',
    `Printed on ${ownerName} stock`,
    'Pick and pack from client stock',
    `Pick and pack from ${ownerName} stock`,
    'Blank garments from client stock',
    `Blank garments from ${ownerName} stock`,
    'Order-change top-ups',
    'Printed then cancelled on client stock',
    `Printed then cancelled on ${ownerName} stock`,
    'Artwork file errors on client stock',
    `Artwork file errors on ${ownerName} stock`,
    ...(viewer === 'owner' ? [`Redone at ${ownerName}'s cost (production errors)`] : []),
    'Location',
    'Time zone',
    'Period start',
    'Period end',
    `Amount in ${currency}`,
  ];
}

export function buildThreePlUsageCsv(
  rows: readonly ThreePlUsageRow[],
  context: ThreePlUsageCsvContext,
): string {
  const { viewer, ownerName, clientsById, location, period, currency } = context;
  const body = rows.map((row) => [
    clientsById.get(row.clientOrgId)?.name ?? '',
    row.sku,
    row.title ?? '',
    row.shipped,
    row.printedOwn,
    row.printedBorrowed,
    row.pickPackOwn,
    row.pickPackBorrowed,
    row.blankOwn,
    row.blankBorrowed,
    row.topUps,
    row.cancelledPrintedOwn,
    row.cancelledPrintedBorrowed,
    row.artworkErrorOwn,
    row.artworkErrorBorrowed,
    ...(viewer === 'owner' ? [row.discardedProductionRedo ?? 0] : []),
    location.name,
    location.timezone,
    period.from,
    period.to,
    row.amount.toFixed(2),
  ]);
  return toCsvContent([threePlUsageCsvHeaders(viewer, ownerName, currency), ...body], {
    eol: '\r\n',
  });
}

export function threePlUsageExportFilename(from: string, to: string): string {
  return `3pl-usage-${from}-to-${to}.csv`;
}
